/**
 * 机器层 —— 模型 / 分辨率 / 同步音频 / 任务名(2026-09-13 A5 改造)。
 *
 * ═══ 2026-09-13 A5:从「折叠」改成常显卡片行 ═══════════════════════════════════════
 * 原来这是一颗默认收起的「模型与机器参数」折叠(P2-3 抽出)。参数杂乱、主次不分的根因之一就是
 * 它把该常看的机器参数藏进折叠、又把不该手填的东西塞在里面。A5 拍板:
 *   ① 展开成常显的**卡片行**(生成模型 / 分辨率 / 同步生成音频 / 任务名,手动选型时多一张生成模式);
 *   ② **退役三控件**(只删 UI,不删 form 字段):
 *      · 口播(直发)Input —— `form.voiceovers` 保留(编排回写/幕二/详情/数字人公共数组),
 *        直接出片没有 burn-in 字幕手填位是可接受的(设计明确「直接出片自己会写口播」老板拍板删);
 *      · 单镜秒数·高级 Input —— `form.durationSec` 保留字段(仍作每镜兜底/裸发回落),只是用户编辑不到;
 *      · 反向 prompt Textarea —— **`DEFAULT_NEGATIVE_PROMPT` 仍静默下发**(form.negativePrompt 初值保留,
 *        buildTaskRequest 照旧发),只是用户编辑不到。
 * 约束派生仍在 useVideoProviderConstraints,本组件只渲染 + 写回 form。
 */
import { Dispatch, SetStateAction } from "react";
import { Settings2 } from "lucide-react";

import { VideoModelSelect } from "../../ui/components/VideoModelSelect";
import { cn } from "@/shared/lib/utils";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Switch } from "@/shared/ui/switch";
import { generationModeLabel } from "@/shared/lib/video/labels";
import { type FormState } from "../../model/videoCreateTypes";
import type {
  GenerationMode,
  VideoCapabilitiesResponse,
  VideoProviderCapability,
  VideoResolution,
} from "../../api/videoService";

interface Props {
  caps: VideoCapabilitiesResponse;
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  effectiveModel: { provider: string; model: string };
  effectiveMode: string;
  selectedCap: VideoProviderCapability | undefined;
  setModelProvider: (provider: string, model: string) => void;
  /**
   * D 批(2026-09-13):放进 ~380px 右栏(目的流两栏 / 自由创作)时传 dense —— 分辨率/音频/任务名
   * 单列堆叠(默认那套 sm:2 / lg:3 网格在窄栏里会挤成小方块)。单列 / 幕二不传,网格照旧。
   */
  dense?: boolean;
}

export function VideoAdvancedSection({
  caps,
  form,
  setForm,
  effectiveModel,
  effectiveMode,
  selectedCap,
  setModelProvider,
  dense,
}: Props) {
  return (
    <section data-testid="video-machine-layer" className="space-y-3">
      <div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-foreground">
        <Settings2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
        模型与机器参数
      </div>

      {/* 卡①:生成模型(智能 / 手动) */}
      <div className="rounded-lg border bg-card/40 p-3">
        <VideoModelSelect
          caps={caps}
          form={form}
          setForm={setForm}
          effectiveModel={effectiveModel}
          effectiveMode={effectiveMode}
          setModelProvider={setModelProvider}
        />
      </div>

      {/* 卡②③④(+ 手动时的生成模式):分辨率 / 同步音频 / 任务名
          ★比例(宽高比)入口不在这里(2026-09-08 收敛,2026-09-13 沿用):统一在画面层一个入口,
            那里写 lockedFields 的口径原样保留。★时长也不在这里(A1 移进「时长×拍法 联动卡」)。 */}
      <div className={cn("grid grid-cols-1 gap-3", !dense && "sm:grid-cols-2 lg:grid-cols-3")}>
        {selectedCap && (
          <div className="rounded-lg border bg-card/40 p-3">
            <Label className="mb-1 block text-xs">分辨率</Label>
            <Select
              value={form.resolution}
              onValueChange={(v) => setForm((s) => ({ ...s, resolution: v as VideoResolution }))}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {selectedCap.supportedResolutions.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {!form.useAutoModel && selectedCap && (
          <div className="rounded-lg border bg-card/40 p-3">
            <Label className="mb-1 block text-xs">生成模式</Label>
            <Select
              value={form.generationMode}
              onValueChange={(v) => setForm((s) => ({ ...s, generationMode: v as GenerationMode }))}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {selectedCap.supportedGenerationModes.map((m) => (
                  <SelectItem key={m} value={m}>
                    {generationModeLabel(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {selectedCap?.supportsGenerateAudio && (
          <div className="flex items-center justify-between rounded-lg border bg-card/40 p-3">
            <div className="min-w-0">
              <div className="text-sm font-medium">生成同步音频</div>
              <div className="text-2xs text-muted-foreground">
                模型生成对白/音效/原声(「综合」「标准」档支持)
              </div>
            </div>
            <Switch
              checked={form.generateAudio}
              onCheckedChange={(v) => setForm((s) => ({ ...s, generateAudio: v }))}
            />
          </div>
        )}

        <div className="rounded-lg border bg-card/40 p-3">
          <Label htmlFor="name" className="mb-1 block text-xs">
            任务名(可选)
          </Label>
          <Input
            id="name"
            className="h-8 text-xs"
            placeholder="留空将自动生成"
            value={form.name}
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            maxLength={200}
          />
        </div>
      </div>
    </section>
  );
}
